'use client';

import { useTranslations } from 'next-intl';
import type { CourtFeed } from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import { platformName } from './streamPlatforms';

/**
 * A feed that cannot play inline: one outbound link to the platform, with what it asks of the
 * viewer. `compact` (the Watch tab) is a short dark card naming the court; the default is the
 * 16:9 box the match page keeps. `live` false (a finished match's scorecard) drops "live" from the
 * compact heading.
 */
export function StreamLinkCard({
  feed,
  compact = false,
  title,
  live = true,
}: {
  feed: CourtFeed;
  compact?: boolean;
  title?: string;
  live?: boolean;
}) {
  const t = useTranslations('EventWatch');
  const platform = platformName(feed.platform, feed.url);
  const button = (
    <a
      href={feed.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium hover:opacity-90"
    >
      {t('watchOn', { platform })}
    </a>
  );
  if (compact) {
    const note =
      feed.platform === 'squashtv'
        ? t('needsSubscription', { platform })
        : feed.platform === 'worldsquash'
          ? t('needsFreeAccount', { platform })
          : t('opensNewTab');
    return (
      <div className="bg-foreground text-background flex w-full flex-col items-start gap-4 rounded-lg p-4 md:p-5">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold tracking-wider uppercase opacity-70">
            {t('streamedOn', { platform })}
          </p>
          <p className="text-lg font-semibold">
            {t(live ? 'watchCourtLive' : 'watchCourtOn', { court: title ?? '', platform })}
          </p>
          <p className="text-sm opacity-80">{note}</p>
        </div>
        {button}
      </div>
    );
  }
  const caption =
    feed.platform === 'squashtv'
      ? t('subscription')
      : feed.platform === 'worldsquash'
        ? t('freeAccount')
        : null;
  return (
    <div className="bg-card border-border flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg border p-4 text-center">
      {button}
      {caption && <p className="text-muted-foreground text-sm">{caption}</p>}
    </div>
  );
}
